import React, { useEffect, useRef, useState } from 'react';
import getCountryFlag from 'src/utils/countries-flags';
import RealWorldMapSvg from '@svg/real-world-map.svg?react';
import * as d3Geo from 'd3-geo';
import { Direction } from '@src/utils/enums';
import DirectionSelect from 'src/components/shared/DirectionSelect/DirectionSelect';
import ArrowRight from '@svg/arrow-right-blue.svg?react';
import { Link } from 'react-router-dom';
import './TopCountriesTrafficWidget.scss';
import { CountryDto } from 'src/dtos/system-configuration.dto';
import { SystemConfiguration } from '@src/utils/system-configuration';
import { formatNumber } from '@src/utils/number-util';
import { TryAgainWidgetMessage } from '@src/components/shared/try-again-message/try-again-widget-message';

interface CountryTraffic {
  countryName: string;
  sessionsCount: number;
}

interface TopCountriesTrafficData {
  totalSessions: number;
  topCountries: CountryTraffic[];
}

export interface TopCountriesTrafficWidgetProps {
  title: string;
  color: string;
  data: TopCountriesTrafficData;
  type: 'allowed' | 'blocked';
  direction: Direction;
  onDirectionChange: (dir: Direction) => void;
  reportPath: string;
  hasError?: boolean;
}

export const TopCountriesTrafficWidget: React.FC<TopCountriesTrafficWidgetProps> = ({
  title,
  color,
  data,
  type,
  direction,
  onDirectionChange,
  reportPath,
  hasError
}) => {
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);

  // Responsive map dimensions based on container width (observe widget-content instead of map container)
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      const width = Math.floor(entry.contentRect.width);
      if (width > 0) setContainerWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const countriesListWidth = 275;
  const mapWidth = Math.max(200, containerWidth - countriesListWidth);
  const aspectRatio = 960 / 420;
  const mapHeight = Math.round(mapWidth / aspectRatio);
  const minRadius = 12;
  const maxRadius = 36;
  const maxSessions = data ? Math.max(...data.topCountries.map(c => c.sessionsCount), 1) : 0;

  const countryDetailsMap: Record<string, CountryDto | undefined> = SystemConfiguration.getInstance().getCountriesMapByOfficialNameIncludeHidden;
  const getCountryDetails = (countryName: string): CountryDto | undefined => countryDetailsMap[countryName];

  // Use original projection (positions scale if entire svg width changes via viewBox scaling)
  const projection = d3Geo.geoNaturalEarth1()
    .scale(170)
    .translate([960 / 2, 580 / 2 - 40])
    .precision(0.1);

  return (
    <div className={`top-countries-traffic-widget ${type}`}>
      <div className="widget-header">
        <div className="widget-title">{title}</div>
        <div className="widget-controls">
          <DirectionSelect
            value={direction}
            onChange={value => onDirectionChange(value as Direction)}
          />
          <Link className="circle-nav" to={reportPath} state={{ direction }}>
            <ArrowRight />
          </Link>
        </div>
      </div>
      {hasError ? <TryAgainWidgetMessage /> :
      <div className="widget-content" ref={containerRef}>
        <div className={`countries-content ${data.totalSessions === 0 ? 'no-data' : ''}`}>
            <div className="total-sessions">
              {data.totalSessions === 0 ? '- No' : <span>{formatNumber(data.totalSessions)}</span>} Sessions
            </div>
            <div className="countries-list">
              {data.topCountries.map((country, idx) => {
                const details = getCountryDetails(country.countryName);
                const Flag = details?.countryCode ? getCountryFlag(details.countryCode) : null;
                const key = details?.countryCode ? details.countryCode : country.countryName;
                return (
                  <div
                    key={key}
                    className={`country-row${hoveredCountry === key ? ' hovered' : ''}`}
                    onMouseEnter={() => setHoveredCountry(key)}
                    onMouseLeave={() => setHoveredCountry(null)}
                  >
                    {Flag && <Flag className="country-flag" />}
                    <span className="country-name">{details?.countryName ?? country.countryName}</span>
                    <span className="country-count">{formatNumber(country.sessionsCount)}</span>
                  </div>
                );
              })}
            </div>
        </div>
        <div className="map-container">
          <svg
            width={mapWidth}
            height={mapHeight}
            className="world-map"
            viewBox="0 0 960 420"
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-label="World map of top countries by traffic"
          >
            <g>
              <RealWorldMapSvg />
            </g>
            {data.topCountries.map((country) => {
              const details = getCountryDetails(country.countryName);
              if (!details?.lat || !details?.lon) return null;
              const coords = projection([details.lon, details.lat]);
              const pos = { x: coords?.[0], y: coords?.[1] };
              const radius =
                minRadius + ((country.sessionsCount / maxSessions) * (maxRadius - minRadius));
              const isHovered = hoveredCountry === details.countryCode;
              return (
                <g key={details.countryCode}>
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={radius}
                    fill={color}
                    fillOpacity={isHovered ? 1 : 0.3}
                    style={{ transition: 'all 0.2s' }}
                  />
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={3.5}
                    fill={isHovered ? '#fff' : color}
                    style={{ transition: 'all 0.2s' }}
                  />
                </g>
              );
            })}
          </svg>
        </div>
      </div>}
    </div>
  );
};
